<template>
    <div>
        <app-template>
            <modal name="modal-rc" width="560px" height="auto" :scrollable="true">
                <div class="p-8">
                    <div class="flex justify-between items-center mb-6 pb-4 border-b">
                        <h2 class="text-xl font-bold flex items-center gap-2">
                            <i class="fas fa-shield-halved text-orange-600"></i>
                            Responsabilité Civile
                        </h2>
                        <button @click="$modal.hide('modal-rc')" class="text-red-600 text-lg">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>

                    <p class="text-xs text-gray-500 mb-5 leading-relaxed">
                        Ces informations apparaîtront sur vos documents officiels.<br>
                        Résultat : <span class="font-mono text-gray-700">{{ rcPreview }}</span>
                    </p>

                    <div class="flex flex-col gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-600 mb-1">
                                Organisme assureur <span class="text-orange-500">*</span>
                            </label>
                            <input v-model="rcForm.organisme" type="text"
                                placeholder="Ex : MAAF, AXA, Generali…"
                                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none" />
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-600 mb-1">
                                Numéro de contrat <span class="text-orange-500">*</span>
                            </label>
                            <input v-model="rcForm.contrat" type="text"
                                placeholder="Ex : 101053256M"
                                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-orange-400 outline-none" />
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-600 mb-1">
                                Zone géographique couverte
                                <span class="text-gray-400 font-normal ml-1">optionnel</span>
                            </label>
                            <input v-model="rcForm.zone" type="text"
                                placeholder="Ex : France Métropolitaine"
                                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none" />
                        </div>

                        <!-- Aperçu temps réel -->
                        <div v-if="rcForm.organisme || rcForm.contrat"
                            class="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono text-gray-600">
                            <span class="text-gray-400 font-sans">Résultat → </span>{{ rcPreview }}
                        </div>
                    </div>

                    <div class="flex justify-end gap-3 mt-6 pt-4 border-t">
                        <button @click="$modal.hide('modal-rc')"
                            class="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                            Annuler
                        </button>
                        <button @click="saveRc"
                            :disabled="!rcForm.organisme || !rcForm.contrat"
                            class="px-4 py-2 text-sm text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-40 rounded-lg transition-colors">
                            <i class="fas fa-check mr-1"></i> Valider
                        </button>
                    </div>
                </div>
            </modal>

            <modal :name="'modal-signature-entreprise'" width="900px" height="auto" :scrollable="true">
                <div class="p-8">

                    <div class="flex justify-between items-center mb-6 pb-4 border-b">
                        <h2 class="text-2xl font-bold flex items-center gap-2">
                            <i class="fas fa-file-signature text-orange-600"></i> Signature de l'entreprise
                        </h2>

                        <button @click="$modal.hide('modal-signature-entreprise')" class="text-red-600 text-lg">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>

                    <div>
                        <div class="border rounded">
                            <div class="grid grid-cols-2">
                                <div class="text-center p-4" :class="selectedSignatureMethod === 'electronique' ? 'font-bold bg-orange-100' : 'cursor-pointer hover:bg-slate-50 duration-200'" @click="selectedSignatureMethod = 'electronique'">
                                    Signature éléctronique
                                </div>
                                <div class="text-center p-4" :class="selectedSignatureMethod === 'fichier' ? 'font-bold bg-orange-100' : 'cursor-pointer hover:bg-slate-50 duration-200'" @click="selectedSignatureMethod = 'fichier'">
                                    Depuis un fichier
                                </div>
                            </div>
                        </div>

                        <div class="mt-4">
                            <template v-if="selectedSignatureMethod === 'electronique'">
                                <input-signature v-model="form.signature_entreprise" @input="$modal.hide('modal-signature-entreprise')" />
                            </template>
                            <template v-else>
                                <div class="space-y-4">
                                    <div class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-orange-400 transition-colors cursor-pointer">
                                        <div class="flex flex-col items-center gap-3">
                                            <div class="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center">
                                                <i class="fas fa-upload text-2xl text-orange-500"></i>
                                            </div>
                                            <div>
                                                <p class="text-sm font-medium text-gray-700 mb-1">Importer une signature</p>
                                                <p class="text-xs text-gray-500">PNG ou JPG accepté</p>
                                            </div>
                                            <input-file key-name="signature-entreprise" v-model="form.signature_entreprise" accept="image/png, image/jpeg" @input="$modal.hide('modal-signature-entreprise')" />
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </modal>

            <div class="max-w-5xl mx-auto px-4 pt-6 pb-20">
                <!-- Header -->
                <div class="mb-8">
                    <h1 class="text-2xl font-semibold text-gray-800">Paramètres de la société</h1>
                    <p class="text-sm text-gray-500">Informations utilisées sur les documents, plannings et factures.</p>
                </div>

                <div v-if="loadingGetData">
                    <div class="flex flex-col gap-4">
                        <div class="bg-white rounded-xl shadow p-6">
                            <div class="h-5 bg-gray-100 rounded w-1/4 mb-4"></div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div class="h-10 bg-gray-100 rounded"></div>
                                <div class="h-10 bg-gray-100 rounded"></div>
                                <div class="h-10 bg-gray-100 rounded"></div>
                                <div class="h-10 bg-gray-100 rounded"></div>
                                <div class="h-10 bg-gray-100 rounded"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="!loadingGetData">
                    <!-- Bloc : Informations générales -->
                    <div class="bg-white rounded-xl shadow p-6 mb-6">
                        <h2 class="text-lg font-medium text-gray-700 mb-4">Informations générales</h2>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

                            <div>
                                <label class="block text-sm font-medium text-gray-600 mb-1">Nom de la société</label>
                                <input v-model="form.nom" type="text"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-600 mb-1">SIRET</label>
                                <input v-model="form.siret" type="text"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-600 mb-1">Numéro de capacité (CERFA)</label>
                                <input v-model="form.num_capacite" type="text"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                            </div>

                        </div>
                    </div>

                    <!-- Bloc : Responsable -->
                    <div class="bg-white rounded-xl shadow p-6 mb-6">
                        <div class="mb-4">
                            <h2 class="text-lg font-medium text-gray-700">Responsabilité de la société</h2>
                            <p class="text-xs font-light text-gray-400">Utilisés pour les signatures importantes des documents.</p>
                        </div>

                        <div class="grid grid-cols-2 gap-4 mb-4">
                            <div class="col-span-2">
                                <label class="block text-sm font-medium text-gray-600 mb-1">Code Responsabilité Civile (RC)</label>

                                <!-- Affichage lecture seule + bouton modifier -->
                                <div class="flex items-center gap-3">
                                    <div class="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-mono text-gray-700 min-h-[36px]">
                                        {{ form.responsable_rc || '—' }}
                                    </div>
                                    <button @click="openRcModal"
                                        class="flex-shrink-0 px-4 py-2 text-sm font-medium text-white bg-slate-700 hover:bg-slate-800 rounded-lg transition-colors">
                                        <i class="fas fa-pencil mr-1"></i>
                                        {{ form.responsable_rc ? 'Modifier' : 'Ajouter' }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-600 mb-1">Prénom du responsable</label>
                                <input v-model="form.responsable_prenom" type="text" placeholder="Prénom"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-600 mb-1">Nom du responsable</label>
                                <input v-model="form.responsable_nom" type="text" placeholder="Nom"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
                            </div>
                        </div>

                        <!-- Capital social -->
                        <div class="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-600 mb-1">
                                    Capital social
                                    <span class="text-gray-400 font-normal ml-1">optionnel</span>
                                </label>
                                <div class="flex items-center gap-2">
                                    <input v-model.number="form.capital_social" type="number" min="0" step="100"
                                        placeholder="0"
                                        class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500" />
                                    <span class="text-sm text-gray-400 flex-shrink-0">€</span>
                                </div>
                                <p v-if="form.capital_social > 0" class="text-xs text-gray-400 mt-1">
                                    → au capital de {{ Number(form.capital_social).toLocaleString('fr-FR') }} €
                                </p>
                            </div>
                        </div>

                        <!-- Signature -->
                        <div class="grid grid-cols-2 gap-4">
                            <template v-if="form.signature_entreprise">
                                <div>
                                    <label class="block text-sm font-medium text-gray-600 mb-1">Signature de l'entreprise</label>
                                    <button @click="$modal.show('modal-signature-entreprise')"
                                        class="mt-2 px-4 py-2 bg-slate-700 hover:bg-slate-800 duration-200 text-white rounded text-xs">
                                        Modifier
                                    </button>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-600 mb-1">Aperçu</label>
                                    <img class="h-[70px]" :src="form.signature_entreprise" alt="">
                                </div>
                            </template>
                            <template v-else>
                                <div class="col-span-2">
                                    <label class="block text-sm font-medium text-gray-600 mb-1">Signature de l'entreprise</label>
                                    <button @click="$modal.show('modal-signature-entreprise')"
                                        class="px-4 py-2 bg-slate-700 hover:bg-slate-800 duration-200 text-white rounded text-xs">
                                        <i class="fas fa-plus mr-1"></i> Ajouter
                                    </button>
                                </div>
                            </template>
                        </div>
                    </div>

                    <!-- Bloc : Adresse -->
                    <div class="bg-white rounded-xl shadow p-6 mb-6">
                        <h2 class="text-lg font-medium text-gray-700 mb-4">Adresse</h2>

                        <adresse-form v-model="adresseForm" :min="true" :context-loc="false" />
                    </div>

                    <!-- Bloc : Horaires -->
                    <div class="bg-white rounded-xl shadow p-6 mb-6">
                        <h2 class="text-lg font-medium text-gray-700 mb-4">Planning</h2>

                        <div class="mb-4">
                            <label class="block text-sm font-medium text-gray-600 mb-2">Jours d'ouverture</label>
                            <div class="flex flex-wrap gap-2">
                                <button
                                    v-for="jour in ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche']"
                                    :key="jour"
                                    type="button"
                                    @click="toggleJour(jour)"
                                    :class="form.open_days.includes(jour)
                                        ? 'bg-orange-500 text-white border-orange-500'
                                        : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'"
                                    class="capitalize px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200">
                                    {{ jour }}
                                </button>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-600 mb-1">Heure d'ouverture</label>
                                <input v-model="form.heure_ouverture" type="time"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-600 mb-1">Heure de fermeture</label>
                                <input v-model="form.heure_fermeture" type="time"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                            </div>
                        </div>
                    </div>

                    <!-- Bloc : Contact -->
                    <div class="bg-white rounded-xl shadow p-6 mb-6">
                        <h2 class="text-lg font-medium text-gray-700 mb-4">Contact</h2>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

                            <div>
                                <label class="block text-sm font-medium text-gray-600 mb-1">Téléphone</label>
                                <input v-model="form.tel" type="text"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-600 mb-1">Email</label>
                                <input v-model="form.mail" type="email"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                            </div>

                        </div>
                    </div>

                    <!-- Bloc : Logo -->
                    <div class="bg-white rounded-xl shadow p-6 mb-6">
                        <h2 class="text-lg font-medium text-gray-700 mb-4">Logo de la société</h2>

                        <input-file key-name="logo-societe" v-model="form.logo" />

                        <div v-if="form.logo" class="mt-4">
                            <img :src="form.logo" class="h-20 object-contain rounded border" />
                        </div>
                    </div>

                    <!-- Bloc : URL Avis Google -->
                    <div v-if="$account.account.societe.abonnement.avis_google" class="bg-white rounded-xl shadow p-6 mb-6">
                        <h2 class="text-lg font-medium text-gray-700 mb-4">Avis Google</h2>

                        <div>
                            <label class="block text-sm font-medium text-gray-600 mb-1">URL de votre page Google entreprise pour que vos clients puissent laisser un avis</label>
                            <input placeholder="https://www.google.com/..." v-model="form.url_google_avis" type="text" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                        </div>
                    </div>

                    <!-- Bloc : URL Avis Google -->
                    <div v-if="$account.account.societe.abonnement.facturation" class="bg-white rounded-xl shadow p-6 mb-6">
                        <h2 class="text-lg font-medium text-gray-700 mb-4">Facturation</h2>

                        <div>
                            <label class="block text-sm font-medium text-gray-600 mb-1">Souhaitez-vous lier la facturation à vos chantiers ?</label>
                            <select v-model="form.manage_facturation" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                                <option :value="1">Oui, je souhaite lier la facturation à mes chantiers</option>
                                <option :value="0">Non, je ne souhaite pas lier la facturation à mes chantiers</option>
                            </select>
                            <div class="text-orange-900 bg-orange-100 mt-2 rounded p-2 border border-orange-200 flex items-center gap-2" v-if="!form.manage_facturation">
                                <i class="fas fa-exclamation-triangle mr-1"></i> 
                                <div>
                                    <p>En choisissant cette option, vous pourrez gérer toute la facturation directement depuis Termoflow.</p>
                                    <p>Sans cette option, les statistiques concernant les coûts et bénéfices ne seront pas accessibles.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
                        <button @click="save" :disabled="loading"
                            class="group relative bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-medium px-8 py-3.5 rounded-full transition-all duration-300 disabled:opacity-50 shadow-2xl hover:shadow-blue-500/50 hover:scale-105 flex items-center gap-2">
                            <svg v-if="!loading" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                            </svg>
                            <svg v-else class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span class="text-base">{{ loading ? 'Enregistrement en cours...' : 'Enregistrer les modifications' }}</span>
                        </button>
                    </div>
                </div>
            </div>

        </app-template>
    </div>
</template>

<script>
import AdresseForm from '@/views/Client/Components/AdresseForm.vue';

export default {
    name: "ParametreSociete",
    components: {
        AdresseForm
    },
    data() {
        return {
            loading: false,

            form: {
                nom: '',
                siret: '',
                tel: '',
                mail: '',
                heure_ouverture: '',
                heure_fermeture: '',
                logo: '',
                url_google_avis: '',
                num_capacite: '',
                tel: '',
                mail: '',
                responsable_prenom: '',
                nom_responsable: '',
                signature_entreprise: '',
                responsable_rc:  '',
                capital_social:  0,
                open_days: [],
                manage_facturation: null,
            },

            rcForm: {
                organisme: '',
                contrat:   '',
                zone:      '',
            },

            adresseForm: {
                adresse1: '',
                adresse2: '',
                code_postal: '',
                ville: '',
                pays: 'France',
                latitude: null,
                longitude: null,
            },

            selectedSignatureMethod: 'electronique',

            loadingGetData: false,
        };
    },

    computed: {
        rcPreview() {
            const parts = [
                this.rcForm.organisme?.trim(),
                this.rcForm.contrat   ? `Contrat n°${this.rcForm.contrat.trim()}` : '',
                this.rcForm.zone?.trim(),
            ].filter(Boolean)
            return parts.join(' - ') || '—'
        },
    },

    mounted() {
        this.load();
    },

    methods: {
        async load() {
            this.loadingGetData = true

            let res = await this.$request.get('/societe/get');
        
            this.form.nom = res.nom;
            this.form.siret = res.siret;
            this.form.tel = res.tel;
            this.form.mail = res.mail;
            this.form.heure_ouverture = res.heure_ouverture;
            this.form.heure_fermeture = res.heure_fermeture;
            this.form.logo = res.logo;
            this.form.url_google_avis = res.url_google_avis;
            this.form.num_capacite = res.num_capacite;
            this.form.tel = res.tel;
            this.form.mail = res.mail;
            this.form.responsable_prenom = res.responsable_prenom;
            this.form.responsable_nom = res.responsable_nom;
            this.form.signature_entreprise = res.signature_entreprise;
            this.form.responsable_rc = res.responsable_rc;
            this.form.open_days = res.open_days ? JSON.parse(res.open_days) : [];
            this.form.manage_facturation = res.manage_facturation;
            this.form.capital_social = res.capital_social || 0

            this.adresseForm.adresse1 = res.adresse;
            this.adresseForm.code_postal = res.code_postal;
            this.adresseForm.ville = res.ville;
            this.adresseForm.pays = res.pays || 'France';

            this.loadingGetData = false
        },

        save() {
            this.loading = true;

            let payload = {
                ...this.form,
                adresse: this.adresseForm.adresse1,
                code_postal: this.adresseForm.code_postal,
                ville: this.adresseForm.ville,
                pays: this.adresseForm.pays,
            }

            payload.open_days = JSON.stringify(payload.open_days);

            this.$request.post('/societe/update', payload)
                .then(() => {
                    window.location.reload();
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        toggleJour(jour) {
            const idx = this.form.open_days.indexOf(jour);
            if (idx === -1) {
                this.form.open_days.push(jour);
            } else {
                this.form.open_days.splice(idx, 1);
            }
        },

        openRcModal() {
            // Parsing de la string existante vers les sous-champs
            const rc = this.form.responsable_rc || ''
            const parts = rc.split(' - ')
            this.rcForm.organisme = parts[0] || ''
            this.rcForm.contrat   = ''
            this.rcForm.zone      = ''

            for (let i = 1; i < parts.length; i++) {
                if (parts[i].startsWith('Contrat n°')) {
                    this.rcForm.contrat = parts[i].replace('Contrat n°', '')
                } else {
                    this.rcForm.zone = parts[i]
                }
            }

            this.$modal.show('modal-rc')
        },

        saveRc() {
            this.form.responsable_rc = this.rcPreview === '—' ? '' : this.rcPreview
            this.$modal.hide('modal-rc')
        },
    }
};
</script>
